import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, ArrowUpRight } from "lucide-react";

export default function PublicProfile() {
    const { userId } = useParams();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);
    const [styles, setStyles] = useState([]);

    useEffect(() => {
        api.get("/styles").then((r) => setStyles(r.data || [])).catch(() => {});
        api.get(`/users/${userId}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
    }, [userId]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-white/50 font-mono-tag text-sm">Loading...</div>;
    if (!p) return <div className="min-h-screen flex items-center justify-center font-display text-3xl">Profile not found</div>;

    const styleLabel = (t) => styles.find((s) => s.id === t)?.label || t;
    const joinedYear = p.joined ? new Date(p.joined).getFullYear() : null;

    return (
        <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20 min-h-screen">
            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ member profile</p>

            <div className="grain-card rounded-md p-6 md:p-10 flex flex-col md:flex-row gap-6 md:gap-10 md:items-center">
                {p.avatar_url ? (
                    <img src={p.avatar_url} alt={p.name} data-testid="public-avatar"
                        className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover border-2 border-brand-saffron" />
                ) : (
                    <div data-testid="public-avatar" className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-brand-ink border-2 border-white/15 flex items-center justify-center">
                        <span className="font-serif-editorial italic text-5xl text-white/25">{p.name?.[0] || "P"}</span>
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h1 data-testid="public-profile-name" className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter uppercase leading-[0.95] break-words">
                        {p.name}
                    </h1>
                    {joinedYear && (
                        <p className="font-mono-tag text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">Member since {joinedYear}</p>
                    )}
                    {p.bio && <p data-testid="public-profile-bio" className="text-white/70 text-base mt-3 max-w-xl">{p.bio}</p>}
                    {(p.style_identity || []).length > 0 && (
                        <div className="mt-5">
                            <p className="font-mono-tag text-[10px] uppercase text-white/40 mb-2">/ style identity</p>
                            <div className="flex flex-wrap gap-2" data-testid="public-style-identity">
                                {p.style_identity.map((t) => (
                                    <span key={t} className="font-mono-tag text-xs uppercase px-3 py-1.5 rounded-sm bg-brand-saffron text-black">{styleLabel(t)}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <section className="mt-14">
                <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ saved looks ({p.saved_looks.length})</p>
                {p.saved_looks.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-md py-16 text-center">
                        <p className="text-white/50 text-sm italic">No saved looks yet.</p>
                    </div>
                ) : (
                    <div data-testid="public-saved-looks" className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {p.saved_looks.map((l) => (
                            <div key={l.id} className="relative aspect-square rounded-md overflow-hidden border border-white/10 group">
                                <img src={l.image_url} alt={l.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                {l.startup_id && (
                                    <Link to={`/startup/${l.startup_id}`} className="absolute inset-0 flex items-end bg-gradient-to-t from-black/80 via-transparent to-transparent">
                                        <span className="p-3 font-mono-tag text-[10px] uppercase text-white/80 truncate">{l.title || "View brand"} <ArrowUpRight className="inline w-3 h-3" /></span>
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="mt-14">
                <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ following ({p.following.length})</p>
                {p.following.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-md py-16 text-center">
                        <p className="text-white/50 text-sm italic">Not following any brands yet.</p>
                    </div>
                ) : (
                    <div data-testid="public-following-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {p.following.map((b) => (
                            <Link key={b.id} to={`/startup/${b.id}`}
                                className="grain-card rounded-md p-4 flex items-center gap-4 hover:border-brand-saffron transition-colors">
                                {(b.preview_images?.[0] || b.banner_url) ? (
                                    <img src={b.preview_images?.[0] || b.banner_url} alt={b.brand_name} className="w-14 h-14 rounded object-cover" />
                                ) : (
                                    <div className="w-14 h-14 rounded bg-brand-ink border border-white/10 flex items-center justify-center font-serif-editorial italic text-xl text-white/25">{b.brand_name?.[0]}</div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-display font-bold text-lg tracking-tight truncate">{b.brand_name}</p>
                                    <p className="text-white/50 text-xs truncate">{b.tagline}</p>
                                </div>
                                <Heart className="w-4 h-4 text-brand-magenta fill-current shrink-0" />
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </main>
    );
}
